import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../Hooks/useAuth';
import { isUserAdmin } from '../../api/trackingConfig';
import InteractionDashboard from './InteractionDashboard';
import TrackingConfigPanel from './TrackingConfigPanel';
import UserRoleManagement from './UserRoleManagement';

/**
 * Admin page component
 * Provides access to admin features like interaction tracking, user management, etc.
 */
function AdminPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Check if the user is an admin
  useEffect(() => {
    const checkAdmin = async () => {
      setLoading(true);
      
      try {
        if (!user) {
          setIsAdmin(false);
          return;
        }
        
        console.log(`AdminPage: Checking if user ${user.id} is an admin`);
        const admin = await isUserAdmin(user.id);
        setIsAdmin(admin);
        console.log(`AdminPage: User ${user.id} is admin: ${admin}`);
      } catch (error) {
        console.error('Error checking admin status:', error);
        // For development, allow access even if the check fails
        setIsAdmin(true);
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [user]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Render the current tab content
  const renderTabContent = () => {
    switch (currentTab) {
      case 0:
        return <InteractionDashboard />;
      case 1:
        return <TrackingConfigPanel />;
      case 2:
        return <UserRoleManagement />;
      default:
        return <InteractionDashboard />;
    }
  };

  // If loading, show loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If not an admin, show access denied
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. Please contact an administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Interaction Dashboard" />
          <Tab label="Tracking Configuration" />
          <Tab label="User Management" />
        </Tabs>
      </Paper>
      
      {renderTabContent()}
    </Box>
  );
}

export default AdminPage;
